import { useState, useEffect } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { 
  getPlatformSettings, updatePlatformSettings, 
  getBillingStatus, payBilling, getPlatformLimits 
} from '../utils/api';
import { useAppSettings } from '../stores/appSettings';
import type { PlatformLimits, SubscriptionPlan } from '@tg-miniapp/shared-types';

export function BillingPage(): React.JSX.Element {
  const { tg, initData } = useTelegram();
  const { currentRole } = useAppSettings();
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({
    prices: { FREE: 0, START: 1490, PRO: 2990 },
    ai_extra: 990
  });

  const [limits, setLimits] = useState<PlatformLimits | null>(null);
  const [subStatus, setSubStatus] = useState<{ plan: SubscriptionPlan, expiresAt: number | null }>({ 
    plan: 'FREE', 
    expiresAt: null 
  });

  useEffect(() => {
    async function load() {
      if (!initData) return;
      try {
        const [settingsRes, limitsRes] = await Promise.all([
          getPlatformSettings(initData),
          getPlatformLimits(initData)
        ]);
        
        if (settingsRes.ok) setPrices(settingsRes.value);
        if (limitsRes.ok) setLimits(limitsRes.value.limits);

        if (currentRole === 'owner') {
          const res = await getBillingStatus(initData);
          if (res.ok) setSubStatus(res.value);
        }
      } catch (e) {
        console.error('Error loading billing data', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [initData, currentRole]);

  const handleSavePrices = async () => {
    const res = await updatePlatformSettings(initData, prices);
    if (res.ok) {
      tg?.HapticFeedback.notificationOccurred('success');
      tg?.showAlert('Цены на платформе обновлены! ✅');
    }
  };

  const handlePay = async (plan: SubscriptionPlan) => {
    if (plan === 'FREE') return;
    setLoading(true);
    try {
      const res = await payBilling(initData, plan);
      if (res.ok) {
        setSubStatus(res.value.status);
        tg?.HapticFeedback.notificationOccurred('success');
        tg?.showAlert(`Тариф ${plan} активирован! ✅`);
      } else {
        tg?.showAlert('Ошибка оплаты: ' + res.error?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-10 text-center animate-pulse">Загрузка тарифов...</div>;

  // --- ЭКРАН ВЛАДЕЛЬЦА (Выбор тарифа) ---
  if (currentRole === 'owner') {
    const plans: { id: SubscriptionPlan; name: string; color: string; desc: string }[] = [
      { id: 'FREE', name: 'Free', color: '#4ade80', desc: 'Для старта' },
      { id: 'START', name: 'Start', color: '#3b82f6', desc: 'Оптимально' },
      { id: 'PRO', name: 'Pro', color: '#a855f7', desc: 'Максимум' },
    ];

    return (
      <div className="flex flex-col gap-6 animate-in slide-in-from-bottom pb-10">
        <div className="px-1">
          <h1 className="text-2xl font-bold">💳 Тарифы</h1>
          <p className="text-sm text-tg-theme-hint">Выберите подходящий уровень для вашего бизнеса</p>
        </div>

        {/* Текущая подписка */}
        <div className="bg-tg-theme-bg p-5 rounded-3xl shadow-xl border border-black/5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 blur-3xl rounded-full -mr-10 -mt-10" />
          <div className="relative z-10">
            <span className="text-[10px] font-bold uppercase tracking-widest text-tg-theme-hint">Текущий тариф</span>
            <div className="text-3xl font-black mt-1 flex items-center gap-2">
              {subStatus.plan} 
              {subStatus.plan !== 'FREE' && <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded-full">Активен</span>}
            </div>
            {subStatus.expiresAt && (
              <div className="text-xs text-tg-theme-hint mt-2">
                Действует до {new Date(subStatus.expiresAt).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* Сетка тарифов */}
        <div className="flex flex-col gap-4">
          {plans.map((plan) => {
            const isCurrent = subStatus.plan === plan.id;
            const price = prices.prices[plan.id as keyof typeof prices.prices];
            const planLimits = limits ? limits[plan.id] : null;

            return (
              <div 
                key={plan.id}
                className={`p-5 rounded-3xl border transition-all ${
                  isCurrent ? 'ring-2 ring-blue-500 border-transparent shadow-lg' : 'border-black/5 bg-tg-theme-bg/50'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: plan.color }}>{plan.name}</h3>
                    <p className="text-xs text-tg-theme-hint">{plan.desc}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-black">{price} ₽</span>
                    <p className="text-[10px] text-tg-theme-hint">в месяц</p>
                  </div>
                </div>

                {planLimits && (
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-green-500">✓</span>
                      <span>{planLimits.aiRequests === -1 ? 'Безлимитный AI' : `${planLimits.aiRequests} AI запросов/мес`}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-green-500">✓</span>
                      <span>До {planLimits.maxClients} клиентов</span>
                    </div>
                    {plan.id !== 'FREE' && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-green-500">✓</span>
                        <span>Приоритетная поддержка</span>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={() => handlePay(plan.id)}
                  disabled={isCurrent || plan.id === 'FREE'}
                  className={`w-full py-3 rounded-2xl font-bold text-sm transition-all active:scale-95 ${
                    isCurrent 
                      ? 'bg-black/5 text-tg-theme-hint cursor-default' 
                      : plan.id === 'FREE' 
                        ? 'hidden' 
                        : 'bg-tg-theme-button text-tg-theme-button-text shadow-lg'
                  }`}
                >
                  {isCurrent ? 'Ваш тариф' : `Перейти на ${plan.name}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // --- ЭКРАН СУПЕР-АДМИНА (Управление ценами) ---
  return (
    <div className="flex flex-col gap-6 pb-20 px-1">
      <div>
        <h1 className="text-2xl font-bold">💎 Цены платформы</h1>
        <p className="text-sm text-tg-theme-hint mt-1">Настройка стоимости подписок для бизнеса</p>
      </div>

      <div className="bg-tg-theme-bg p-5 rounded-3xl shadow-sm border border-black/5 flex flex-col gap-4">
        {(['START', 'PRO'] as const).map(p => (
          <div key={p}>
            <label className="text-[10px] font-bold text-tg-theme-hint uppercase mb-2 block">{p} Тариф (₽/мес)</label>
            <div className="flex items-center gap-3 bg-black/5 rounded-2xl px-4 py-3 border border-transparent focus-within:border-blue-500 transition-all">
              <input 
                type="number"
                className="bg-transparent border-none outline-none text-lg font-bold w-full"
                value={prices.prices[p]}
                onChange={e => setPrices({
                  ...prices,
                  prices: { ...prices.prices, [p]: parseInt(e.target.value) }
                })}
              />
              <span className="font-bold opacity-30">₽</span>
            </div>
          </div>
        ))}

        <button
          onClick={handleSavePrices}
          className="bg-blue-500 text-white font-bold py-4 rounded-2xl text-sm shadow-xl shadow-blue-500/20 active:scale-95 transition-transform"
        >
          Обновить цены на платформе
        </button>
      </div>
      
      <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 rounded-2xl">
        <p className="text-xs text-yellow-600 dark:text-yellow-400">
          💡 Лимиты запросов и клиентов для этих тарифов настраиваются в разделе <b>AI Агенты</b>.
        </p>
      </div>
    </div>
  );
}
